import { z } from 'zod';
import { governanceLifecycleStatusSchema } from './governance-standards-workflows.js';
export const policyProposalKindSchema = z.enum([
    'discovered_rule',
    'discovered_rule_update',
    'pattern_proposal',
    'adr',
    'coding_standard',
]);
export const policyProposalPhaseSchema = z.enum(['discover', 'propose']);
export const policyProposalStatusSchema = z.enum([
    'pending',
    'snoozed',
    'approved',
    'rejected',
    'review_only',
    'stale',
]);
export const policyProposalItemSchema = z.object({
    /** Stable composite id: virtual `{kind}:{entityId}` or durable `policy_proposal:{uuid}`. */
    id: z.string().min(1),
    kind: policyProposalKindSchema,
    phase: policyProposalPhaseSchema,
    title: z.string(),
    excerpt: z.string().nullable(),
    source: z.enum(['active_rules_discovery', 'governance', 'pattern_discovery']),
    patternFamily: z.string().nullable().optional(),
    recommendedArtifactKind: z.enum(['adr', 'coding_standard', 'rule']).nullable().optional(),
    confidence: z.number().min(0).max(1).nullable(),
    severity: z.string().nullable(),
    lifecycleStatus: z.string().nullable(),
    scopeLevel: z.string(),
    repositoryId: z.string().uuid().nullable(),
    repositoryLabel: z.string().nullable(),
    stackProfileId: z.string().uuid().nullable(),
    entityId: z.string().uuid(),
    updatedAt: z.string(),
    deepLink: z.string(),
    /** Present on durable `policy_proposal:{uuid}` rows (412). */
    proposalVersion: z.number().int().min(1).optional(),
    supportingSignalCount: z.number().int().min(1).nullable().optional(),
    whyItMatters: z.string().nullable().optional(),
    normativeBody: z.string().nullable().optional(),
    enforcementRules: z.array(z.string()).optional(),
    sampleRules: z.array(z.string()).optional(),
    /** Repositories that contributed discovery signals (when synthesized from multiple repos). */
    affectedRepositoryIds: z.array(z.string().uuid()).optional(),
});
export const policyProposalsSummarySchema = z.object({
    pendingCount: z.number().int(),
    discoveredRuleCount: z.number().int(),
    proposedAdrCount: z.number().int(),
    proposedCodingStandardCount: z.number().int(),
    patternProposalCount: z.number().int(),
    synthesizedCandidateCount: z.number().int().optional(),
    governanceCandidateCount: z.number().int().optional(),
    ruleUpdateCount: z.number().int().optional(),
});
export const policyProposalsBundleSchema = z.object({
    summary: policyProposalsSummarySchema,
    items: z.array(policyProposalItemSchema),
    detectedPracticesSection: z
        .object({
        title: z.string(),
        lastScanAt: z.string().datetime().nullable().optional(),
        items: z.array(z.object({
            label: z.string(),
            confidence: z.number(),
            sourcePath: z.string(),
            linkedProposalId: z.string().min(1).nullable().optional(),
        })),
    })
        .optional(),
    ruleUpdatesSection: z
        .object({
        title: z.string(),
        items: z.array(policyProposalItemSchema),
    })
        .optional(),
});
export const policyProposalDecisionSchema = z.enum(['approve', 'reject', 'snooze', 'review_only']);
export const policyProposalScopeLevelSchema = z.enum([
    'organization',
    'repository',
    'stack_profile',
]);
export const policyProposalDecideBodySchema = z
    .object({
    decision: policyProposalDecisionSchema,
    scopeLevel: policyProposalScopeLevelSchema.optional(),
    repositoryId: z.string().uuid().nullable().optional(),
    stackProfileId: z.string().uuid().nullable().optional(),
    expectedUpdatedAt: z.string().datetime().optional(),
    expectedProposalVersion: z.number().int().min(1).optional(),
    snoozeUntil: z.string().datetime().optional(),
})
    .strict();
export const policyProposalDecideResponseSchema = z.object({
    id: z.string(),
    decision: policyProposalDecisionSchema,
    outcome: z.enum(['applied', 'rejected', 'conflict', 'not_found']),
    /** Govern artifact lifecycle (virtual adr/coding_standard queue). */
    lifecycleStatus: governanceLifecycleStatusSchema.optional(),
    /** Durable proposal row status after decision (412). */
    proposalStatus: policyProposalStatusSchema.optional(),
    promotedArtifactKind: z.enum(['adr', 'coding_standard', 'review_rule']).optional(),
    promotedArtifactId: z.string().uuid().optional(),
    promotedArtifactDeepLink: z.string().optional(),
});
export const policyProposalDraftPatchSchema = z
    .object({
    title: z.string().min(8).max(512).optional(),
    normativeBody: z.string().min(1).max(65_536).optional(),
    enforcementRules: z.array(z.string().min(8).max(512)).min(1).max(10).optional(),
    expectedProposalVersion: z.number().int().min(1),
})
    .strict();
export const policyProposalDraftPatchResponseSchema = z.object({
    id: z.string(),
    proposalVersion: z.number().int().min(1),
    outcome: z.enum(['updated', 'conflict', 'not_found']),
});
//# sourceMappingURL=govern-policy-proposals.js.map